import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dumbbell, Plus, X, Activity, Moon } from 'lucide-react';

interface ExerciseSectionProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

export const ExerciseSection = ({ data, onChange }: ExerciseSectionProps) => {
  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const addActivity = () => {
    const activities = data.activity_plan || [];
    activities.push({
      activity: '',
      frequency_per_week: 0,
      avg_duration_min: 0,
      intensity: 'moderate',
      scheduled_date: '',
      completed: false,
      notes: '',
      frequency: 'weekly',
      days: [],
      monthly_day: '',
      custom_dates: []
    });
    handleChange('activity_plan', activities);
  };

  const updateActivity = (index: number, field: string, value: any) => {
    const activities = [...(data.activity_plan || [])];
    activities[index] = { ...activities[index], [field]: value };
    handleChange('activity_plan', activities);
  };

  const removeActivity = (index: number) => {
    const activities = data.activity_plan || [];
    activities.splice(index, 1);
    handleChange('activity_plan', activities);
  };

  const intensityLevels = [
    { value: 'low', label: 'Bassa' },
    { value: 'moderate', label: 'Moderata' },
    { value: 'high', label: 'Alta' }
  ];

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <Dumbbell className="w-6 h-6 text-orange-500" />
        <h2 className="text-2xl font-bold text-white">Esercizio e Benessere</h2>
      </div>

      {/* Activity Plan */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Piano di Attività
            </div>
            <Button onClick={addActivity} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(data.activity_plan || []).map((activity: any, index: number) => (
            <div key={index} className="p-4 border border-white/10 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-white">Attività #{index + 1}</h4>
                <Button
                  onClick={() => removeActivity(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Tipo Attività</Label>
                  <Input
                    value={activity.activity}
                    onChange={(e) => updateActivity(index, 'activity', e.target.value)}
                    placeholder="es. Passeggiata, Corsa, Nuoto"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Intensità</Label>
                  <Select
                    value={activity.intensity}
                    onValueChange={(value) => updateActivity(index, 'intensity', value)}
                  >
                    <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                      {intensityLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Frequenza (volte/settimana)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="7"
                    value={activity.frequency_per_week}
                    onChange={(e) => updateActivity(index, 'frequency_per_week', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
                 <div>
                   <Label className="text-white">Durata Media (minuti)</Label>
                   <Input
                     type="number"
                     min="5"
                     value={activity.avg_duration_min}
                     onChange={(e) => updateActivity(index, 'avg_duration_min', parseInt(e.target.value) || 0)}
                     className="mt-1"
                   />
                 </div>
               </div>
               
               <div className="space-y-3">
                 <Label className="text-white">Pianificazione Esercizio</Label>
                 <Select value={activity.frequency || 'weekly'} onValueChange={(value) => updateActivity(index, 'frequency', value)}>
                   <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                     <SelectValue placeholder="Frequenza" />
                   </SelectTrigger>
                   <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                     <SelectItem value="daily">Giornaliero</SelectItem>
                     <SelectItem value="weekly">Settimanale</SelectItem>
                     <SelectItem value="monthly">Mensile</SelectItem>
                     <SelectItem value="specific">Date Specifiche</SelectItem>
                   </SelectContent>
                 </Select>
                 
                 {activity.frequency === 'weekly' && (
                   <div>
                     <Label className="text-white">Giorni della Settimana</Label>
                     <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mt-2">
                       {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map((day, dayIndex) => (
                         <label key={day} className="flex items-center gap-1 text-sm text-white">
                           <input
                             type="checkbox"
                             checked={(activity.days || []).includes(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex])}
                             onChange={(e) => {
                               const englishDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                               const currentDays = activity.days || [];
                               const englishDay = englishDays[dayIndex];
                               if (e.target.checked) {
                                 updateActivity(index, 'days', [...currentDays, englishDay]);
                               } else {
                                 updateActivity(index, 'days', currentDays.filter((d: string) => d !== englishDay));
                               }
                             }}
                           />
                           {day.slice(0, 3)}
                         </label>
                       ))}
                     </div>
                   </div>
                 )}
                 
                 {activity.frequency === 'monthly' && (
                   <div>
                     <Label className="text-white">Giorno del Mese</Label>
                     <Input
                       type="number"
                       min="1"
                       max="31"
                       value={activity.monthly_day || ''}
                       onChange={(e) => updateActivity(index, 'monthly_day', e.target.value)}
                       placeholder="es. 15 (ogni 15 del mese)"
                       className="mt-1"
                     />
                   </div>
                 )}
                 
                 {activity.frequency === 'specific' && (
                   <div>
                     <Label className="text-white">Date Specifiche</Label>
                     <div className="space-y-2 mt-2">
                       {(activity.custom_dates || []).map((date: string, dateIndex: number) => (
                         <div key={dateIndex} className="flex items-center gap-2">
                           <Input
                             type="date"
                             value={date}
                             onChange={(e) => {
                               const newDates = [...(activity.custom_dates || [])];
                               newDates[dateIndex] = e.target.value;
                               updateActivity(index, 'custom_dates', newDates);
                             }}
                           />
                           <Button
                             type="button"
                             variant="ghost"
                             size="sm"
                             onClick={() => {
                               const newDates = activity.custom_dates || [];
                               newDates.splice(dateIndex, 1);
                               updateActivity(index, 'custom_dates', newDates);
                             }}
                           >
                             <X className="w-4 h-4" />
                           </Button>
                         </div>
                       ))}
                       <Button
                         type="button"
                         variant="outline"
                         size="sm"
                         onClick={() => {
                           const currentDates = activity.custom_dates || [];
                           updateActivity(index, 'custom_dates', [...currentDates, '']);
                         }}
                       >
                         <Plus className="w-4 h-4 mr-2" />
                         Aggiungi Data
                       </Button>
                     </div>
                   </div>
                 )}
               </div>
            </div>
          ))}
          
          {(!data.activity_plan || data.activity_plan.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna attività programmata</p>
              <p className="text-sm">Clicca "Aggiungi" per creare il piano di attività</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sleep and Rest */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Moon className="w-5 h-5" />
            Riposo e Sonno
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="avg_sleep_hours" className="text-white">Ore di Sonno Medie</Label>
              <Input
                id="avg_sleep_hours"
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={data.sleep_rest?.avg_sleep_hours || ''}
                onChange={(e) => handleChange('sleep_rest', { 
                  ...data.sleep_rest, 
                  avg_sleep_hours: parseFloat(e.target.value) || 0 
                })}
                placeholder="12"
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <div className="w-full">
                <Label className="text-white">Qualità del Riposo</Label>
                <Select 
                  value={data.sleep_rest?.quality || ''} 
                  onValueChange={(value) => handleChange('sleep_rest', { 
                    ...data.sleep_rest, 
                    quality: value 
                  })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleziona qualità" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Eccellente</SelectItem>
                    <SelectItem value="good">Buona</SelectItem>
                    <SelectItem value="fair">Discreta</SelectItem>
                    <SelectItem value="poor">Scarsa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <div>
            <Label htmlFor="sleep_notes" className="text-white">Note sul Riposo</Label>
            <Textarea
              id="sleep_notes"
              value={data.sleep_rest?.notes || ''}
              onChange={(e) => handleChange('sleep_rest', { 
                ...data.sleep_rest, 
                notes: e.target.value 
              })}
              placeholder="Abitudini del sonno, luoghi preferiti per riposare..."
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Enrichment Activities */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Arricchimento Ambientale</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="toys_games" className="text-white">Giochi e Giocattoli</Label>
            <Textarea
              id="toys_games"
              value={data.toys_games || ''}
              onChange={(e) => handleChange('toys_games', e.target.value)}
              placeholder="Lista dei giochi preferiti, puzzle, giocattoli interattivi..."
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="mental_stimulation" className="text-white">Stimolazione Mentale</Label>
            <Textarea
              id="mental_stimulation"
              value={data.mental_stimulation || ''}
              onChange={(e) => handleChange('mental_stimulation', e.target.value)}
              placeholder="Attività di nosework, training cognitivo, puzzle alimentari..."
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="social_activities" className="text-white">Attività Sociali</Label>
            <Textarea
              id="social_activities"
              value={data.social_activities || ''}
              onChange={(e) => handleChange('social_activities', e.target.value)}
              placeholder="Interazioni con altri animali, socializzazione..."
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Exercise Notes */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Note Generali</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="exercise_notes" className="text-white">Note sull'Esercizio e Benessere</Label>
            <Textarea
              id="exercise_notes"
              value={data.general_notes || ''}
              onChange={(e) => handleChange('general_notes', e.target.value)}
              placeholder="Osservazioni generali su comportamento, preferenze, limitazioni fisiche..."
              className="mt-1"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};